/**
 * The catalog — the tenant's approved event types, plus anything blocked.
 *
 * One row per type. The columns answer the questions someone actually opens
 * this page with: what is it, who can build on it, does it have a usable
 * payload, and is anything listening to it.
 */
import { MoreVertical, TriangleAlert } from 'lucide-react';
import {
  Banner,
  Button,
  Card,
  IconButton,
  Popover,
  Stat,
  Table,
} from '@servicecompressionllc/sc-design-system';
import type { TenantEventType } from '../../lib/types.js';
import { ScopeSummary } from '../../components/ScopeSummary.js';
import { formatAge } from './EventsPage.js';

interface Props {
  approved: TenantEventType[];
  /** Held types drive the banner — the count is the reason to switch tabs. */
  heldCount: number;
  onReviewHeld: () => void;
  onRegisterNew: () => void;
  /** Opens the drawer in edit mode — description and scopes are changeable,
   *  the type string is not. */
  onEdit: (entry: TenantEventType) => void;
  /** Stop routing but keep accepting — the type returns to Needs review. */
  onDisable: (entry: TenantEventType) => void;
  /** Refuse this type at the door from now on — known spam. */
  onBlock: (entry: TenantEventType) => void;
}

/**
 * What a workflow can reference, and whether anything is being dropped.
 *
 * `6 declared` is the plain case. `· 2 dropped` is the one that matters: the
 * sender is sending fields nobody declared, so a variable someone expects is
 * silently absent. `Nothing declared` means the type routes nowhere at all.
 */
const PayloadCell = ({ entry }: { entry: TenantEventType }) => {
  const declared = entry.declaredFields?.fields.length ?? 0;
  const extra = entry.undeclaredFields.length;
  // No declared list at all means nothing routes: only declared fields reach a
  // workflow, so a type with none has nothing to hand one. The waiting count is
  // the actionable part.
  if (entry.declaredFields === null) {
    return (
      <span className="text-sm">
        <span className="text-ink-subtle">Nothing declared</span>
        {entry.heldCount > 0 && <span className="text-ink"> · {entry.heldCount} waiting</span>}
      </span>
    );
  }
  // An EMPTY declared list is the opposite state, not the same one: a doorbell
  // event, registered on purpose as carrying no data, and routing fine.
  if (declared === 0) {
    return <span className="text-ink-muted text-sm">Carries no data</span>;
  }
  return (
    <span className="text-sm">
      <span className="text-ink">{declared} declared</span>
      {extra > 0 && (
        <span className="text-ink-muted">
          {' · '}
          {extra} dropped
        </span>
      )}
    </span>
  );
};

export const EventCatalogTab = ({
  approved,
  heldCount,
  onReviewHeld,
  onRegisterNew,
  onEdit,
  onDisable,
  onBlock,
}: Props) => {
  // Card's padding defaults to "none", so an empty state has to bring its own
  // or the copy sits flush against the border.
  if (approved.length === 0) {
    return (
      <Card className="px-6 py-12 text-center">
        <p className="text-ink text-sm font-semibold">No event types yet</p>
        <p className="text-ink-muted mx-auto mt-2 max-w-[52ch] text-sm">
          Types appear here as soon as something sends them. You can also declare one up front and
          hand the endpoint to whoever owns the sending system.
        </p>
        <Button className="mt-5" onClick={onRegisterNew}>
          Register event
        </Button>
      </Card>
    );
  }

  const received24h = approved.reduce((n, t) => n + t.received24h, 0);
  const unused = approved.filter((t) => t.workflowCount === 0).length;

  return (
    <div className="flex flex-col gap-5">
      {heldCount > 0 && (
        <Banner variant="info">
          <TriangleAlert className="h-4 w-4 flex-shrink-0" />
          <Banner.Body>
            <Banner.Title>
              {heldCount} new event type{heldCount === 1 ? '' : 's'}{' '}
              {heldCount === 1 ? 'is' : 'are'} waiting on you
            </Banner.Title>
            <p className="text-ink-muted mt-0.5 text-sm">
              Something is sending these but they aren’t in the catalog, so they’re held. Nothing
              was dropped and no workflow ran.
            </p>
          </Banner.Body>
          <button
            type="button"
            className="text-info self-center text-sm font-semibold"
            onClick={onReviewHeld}
          >
            Review →
          </button>
        </Banner>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Approved types" value={String(approved.length)} />
        <Stat label="Needs review" value={String(heldCount)} />
        <Stat label="Received" value={received24h.toLocaleString()} sub="last 24h" />
        <Stat label="Not used by a workflow" value={String(unused)} />
      </div>

      {/* No `overflow-hidden` on the card OR the table: either one clips the
          row menu, which is absolutely positioned inside a cell. */}
      <Card padding="none">
        <Table.Root className="w-full text-sm">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Event</Table.HeaderCell>
              <Table.HeaderCell>Who can use it</Table.HeaderCell>
              <Table.HeaderCell>Payload</Table.HeaderCell>
              <Table.HeaderCell>Workflows</Table.HeaderCell>
              <Table.HeaderCell>Last seen</Table.HeaderCell>
              <Table.HeaderCell> </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {approved.map((entry) => (
              <Table.Row key={entry.id}>
                <Table.Cell>
                  <div className="text-ink font-mono text-sm">{entry.type}</div>
                  {entry.description && (
                    <div className="text-ink-muted mt-0.5 max-w-[52ch] truncate text-xs">
                      {entry.description}
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {/* Same read-only summary users, groups and API keys use: a
                      badge per scope wrapped this cell onto two lines and made
                      the row height depend on how many scopes were picked. */}
                  {entry.scopes.length === 0 ? (
                    <span className="text-ink-subtle text-sm">Anyone</span>
                  ) : (
                    <ScopeSummary scopes={entry.scopes} />
                  )}
                </Table.Cell>
                <Table.Cell>
                  <PayloadCell entry={entry} />
                </Table.Cell>
                <Table.Cell>
                  {/* A type nothing listens for is the actionable case — it is
                      being accepted and going nowhere. */}
                  <span
                    className={`text-sm ${entry.workflowCount === 0 ? 'text-ink-subtle' : 'text-ink'}`}
                  >
                    {entry.workflowCount === 0 ? 'none' : entry.workflowCount}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-ink-muted text-sm">
                    {entry.lastSeenAt ? formatAge(entry.lastSeenAt) : 'never'}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <Popover>
                    <Popover.Trigger asChild>
                      <IconButton aria-label={`Actions for ${entry.type}`} size="sm">
                        <MoreVertical />
                      </IconButton>
                    </Popover.Trigger>
                    {/* `align="end"` because this is the last column: the
                        default `left-0` opens the panel off the viewport. */}
                    <Popover.Content align="end" className="z-50 w-56">
                      <ul className="py-1">
                        <Popover.Item onClick={() => onEdit(entry)}>Edit</Popover.Item>
                        <Popover.Item onClick={() => onDisable(entry)}>Disable</Popover.Item>
                        <Popover.Item className="text-danger" onClick={() => onBlock(entry)}>
                          Block this type
                        </Popover.Item>
                      </ul>
                    </Popover.Content>
                  </Popover>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Card>
    </div>
  );
};
